// src/components/mention/NoteReactions.jsx
import React, { useMemo, useState } from 'react';
import { FiSmile, FiX } from 'react-icons/fi';
import {
  FaThumbsUp,
  FaHeart,
  FaLaughBeam,
  FaSurprise,
  FaSadTear,
  FaPrayingHands
} from 'react-icons/fa';

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ⚠️ Agar aapka backend route alag hai to sirf yahan badlein
const REACT_URL = (caseId, noteId) =>
  `${API_BASE_URL}/api/cases/${caseId}/notes/${noteId}/react`;

// Backend ko yehi values jaati hain (unchanged), UI mein icon dikhta hai
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const REACTION_META = {
  '👍': { Icon: FaThumbsUp, color: '#0d6efd', label: 'Like' },
  '❤️': { Icon: FaHeart, color: '#e03131', label: 'Love' },
  '😂': { Icon: FaLaughBeam, color: '#f59f00', label: 'Haha' },
  '😮': { Icon: FaSurprise, color: '#f08c00', label: 'Wow' },
  '😢': { Icon: FaSadTear, color: '#4263eb', label: 'Sad' },
  '🙏': { Icon: FaPrayingHands, color: '#2f9e44', label: 'Thanks' }
};

// emoji variation selector (❤ vs ❤️) ko normalize karne ke liye
const getMeta = (emoji) =>
  REACTION_META[emoji] ||
  REACTION_META[String(emoji || '').replace(/\uFE0F/g, '')] ||
  REACTION_META[`${String(emoji || '').replace(/\uFE0F/g, '')}\uFE0F`] ||
  null;

const ReactionIcon = ({ emoji, size = 14 }) => {
  const meta = getMeta(emoji);
  if (!meta) return <span>{emoji}</span>; // unknown emoji fallback
  const { Icon, color } = meta;
  return <Icon size={size} color={color} />;
};

// ✅ Token ke saath API call
const sendReaction = async (caseId, noteId, emoji) => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    '';

  let info = {};
  try {
    info = JSON.parse(localStorage.getItem('userInfo') || '{}');
  } catch (e) {
    /* ignore */
  }

  const userId =
    localStorage.getItem('userId') || info.userId || info._id || '';
  const userName =
    localStorage.getItem('userName') || info.userName || info.name || '';

  const res = await fetch(REACT_URL(caseId, noteId), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ emoji, userId, userName })
  });
  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    /* ignore non-json body */
  }

  if (!res.ok) {
    throw new Error(
      data?.message ||
        data?.error ||
        (res.status === 401 ? 'Session expired, please login again' : 'Failed to react')
    );
  }
  return data;
};

const NoteReactions = ({ caseId, noteId, reactions, canReact, myUserId, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const list = Array.isArray(reactions) ? reactions : [];

  const { groups, myEmoji } = useMemo(() => {
    const map = new Map();
    let mine = null;

    list.forEach((r) => {
      if (!r || !r.emoji) return;
      const isMine =
        myUserId !== null &&
        myUserId !== undefined &&
        r.userId !== undefined &&
        Number(r.userId) === myUserId;
      if (isMine) mine = r.emoji;

      if (!map.has(r.emoji)) {
        map.set(r.emoji, { emoji: r.emoji, count: 0, names: [], mine: false });
      }
      const g = map.get(r.emoji);
      g.count += 1;
      g.names.push(r.userName || 'User');
      if (isMine) g.mine = true;
    });

    return { groups: Array.from(map.values()), myEmoji: mine };
  }, [list, myUserId]);

  // nothing to show and nothing to do -> render nothing
  if (!canReact && groups.length === 0) return null;

  const handlePick = async (emoji) => {
    if (busy || !canReact) return;
    setBusy(true);
    setError('');
    setOpen(false);

    try {
      const data = await sendReaction(caseId, noteId, emoji);
      if (data?.success && typeof onUpdated === 'function') {
        onUpdated(noteId, data.reactions || []);
      } else if (data && data.success === false) {
        setError(data.message || 'Failed to react');
      }
    } catch (e) {
      setError(e.message || 'Failed to react');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.wrap}>
      {groups.map((g) => (
        <button
          key={g.emoji}
          type="button"
          disabled={!canReact || busy}
          onClick={() => handlePick(g.emoji)}
          title={g.names.join(', ')}
          style={{
            ...s.chip,
            ...(g.mine ? s.chipMine : {}),
            cursor: canReact ? 'pointer' : 'default'
          }}
        >
          <ReactionIcon emoji={g.emoji} size={13} />
          <span style={s.count}>{g.count}</span>
        </button>
      ))}

      {canReact &&
        (open ? (
          <div style={s.picker}>
            {REACTION_EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => handlePick(e)}
                title={getMeta(e)?.label || ''}
                style={{ ...s.emojiBtn, ...(myEmoji === e ? s.emojiBtnActive : {}) }}
              >
                <ReactionIcon emoji={e} size={16} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={s.closeBtn}
              title="Close"
            >
              <FiX size={12} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={busy}
            style={s.addBtn}
            title="React to this note"
          >
            <FiSmile size={13} />
            {groups.length === 0 && <span>React</span>}
          </button>
        ))}

      {error && <span style={s.error}>{error}</span>}
    </div>
  );
};

const s = {
  wrap: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    marginTop: '10px'
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    background: '#f1f3f5',
    border: '1px solid #dee2e6',
    borderRadius: '12px',
    padding: '2px 9px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  chipMine: { background: '#e8f4f8', border: '1px solid #8fc1f0' },
  count: { fontSize: '11px', fontWeight: 600, color: '#495057' },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'white',
    border: '1px dashed #adb5bd',
    color: '#6c757d',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  picker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    background: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '16px',
    padding: '2px 6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  emojiBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emojiBtnActive: { background: '#e8f4f8' },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#868e96',
    cursor: 'pointer',
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center'
  },
  error: { fontSize: '11px', color: '#dc3545' }
};

export default React.memo(NoteReactions);